import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { baseUrl } from "../../helpers/constants";
import { getCookie, signout } from "../../helpers/cookies";
import {
    parseCustomerDeletionImpact,
    parseCustomerMasterDetails,
    parseCustomerMasterList,
    parseCustomerMasterStats,
} from "./customerMasterParser";

const customFetchBaseQuery = fetchBaseQuery({
    baseUrl: baseUrl,
    prepareHeaders: (headers, { getState }) => {
        const token = getCookie("magiclock_admin_token");
        if (token) {
            headers.set("Authorization", `BEARER ${token}`);
        }
        return headers;
    },
});

const baseQueryWithReauth = async (args, api, extraOptions) => {
    let result = await customFetchBaseQuery(args, api, extraOptions);

    if (result.error && result.error.status === 403) {
        signout(() => {
            window.location.href = "/login";
        });
    }

    return result;
};

const customerMasterApis = createApi({
    reducerPath: "customerMasterApis",
    baseQuery: baseQueryWithReauth,
    keepUnusedDataFor: 0,
    tagTypes: ["CustomerMaster", "CustomerMasterDetails", "CustomerDeletionImpact"],
    endpoints: (build) => ({
        // Admin customer master search
        getAdminCustomerMasterSearch: build.query({
            query: (params) => {
                // Remove unsupported parameters
                const { sort_by, sort_order, search, ...validParams } = params;
                return {
                    url: "/users/customer-master/search",
                    method: "GET",
                    params: validParams,
                };
            },
            transformResponse: (response) => parseCustomerMasterList(response),
            providesTags: ["CustomerMaster"],
            keepUnusedDataFor: 5,
        }),
        // Get customer master details by ID
        getAdminCustomerMasterDetails: build.query({
            query: (id) => ({
                url: `/users/customer-master/${id}`,
                method: "GET",
            }),
            transformResponse: (response) => parseCustomerMasterDetails(response),
            providesTags: ["CustomerMasterDetails"],
            keepUnusedDataFor: 5,
        }),
        // Get customer master details by mobile
        getAdminCustomerMasterByMobile: build.query({
            query: (mobileNumber) => ({
                url: `/users/customer-master/mobile/${mobileNumber}`,
                method: "GET",
            }),
            transformResponse: (response) => parseCustomerMasterDetails(response),
            providesTags: ["CustomerMasterDetails"],
            keepUnusedDataFor: 5,
        }),
        // Get customer master statistics
        getAdminCustomerMasterStats: build.query({
            query: () => ({
                url: "/users/customer-master/stats",
                method: "GET",
            }),
            transformResponse: (response) => parseCustomerMasterStats(response),
            providesTags: ["CustomerMaster"],
            keepUnusedDataFor: 5,
        }),
        // Get customer deletion impact before confirmation
        getCustomerDeletionImpact: build.query({
            query: (id) => ({
                url: `/users/customer-master/${id}/delete-impact`,
                method: "GET",
            }),
            transformResponse: (response) => parseCustomerDeletionImpact(response),
            providesTags: ["CustomerDeletionImpact"],
        }),
        // Delete customer with backup snapshot
        deleteCustomerMaster: build.mutation({
            query: ({ id, deletion_reason = null }) => ({
                url: `/users/customer-master/${id}`,
                method: "DELETE",
                params: deletion_reason ? { deletion_reason } : undefined,
            }),
            invalidatesTags: ["CustomerMaster", "CustomerMasterDetails", "CustomerDeletionImpact"],
        }),
    }),
});

export const {
    useGetAdminCustomerMasterSearchQuery,
    useGetAdminCustomerMasterDetailsQuery,
    useGetAdminCustomerMasterByMobileQuery,
    useGetAdminCustomerMasterStatsQuery,
    useLazyGetCustomerDeletionImpactQuery,
    useDeleteCustomerMasterMutation,
} = customerMasterApis;
export default customerMasterApis;
